// components/events/FundsPanel.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Card, Row, Col, Form, Button, Table, Spinner, Alert, Badge, Nav, Modal,
} from "react-bootstrap";
import { db } from "../../firebase";
import {
  collection, query, orderBy, onSnapshot,
  addDoc, deleteDoc, doc, serverTimestamp, writeBatch, updateDoc,
} from "firebase/firestore";
import { fmt, fmtTs, cleanObject, calculateAutoAmount } from "../../utils/eventUtils";
import * as XLSX from "xlsx";

/* ---------------------------------
   Helpers
---------------------------------- */

// Safe string compare (case-insensitive)
const cmpStr = (a, b) => String(a || "").localeCompare(String(b || ""), undefined, { sensitivity: "base" });

// Parse age from number / Date / Firestore Timestamp / ISO
const getAge = (value) => {
  if (value == null) return null;
  if (typeof value === "number" && Number.isFinite(value)) return value >= 0 ? value : null;
  try {
    const d =
      typeof value?.toDate === "function"
        ? value.toDate()
        : typeof value === "string"
        ? new Date(value)
        : value instanceof Date
        ? value
        : null;
    if (!d || Number.isNaN(d.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
    return age >= 0 ? age : null;
  } catch {
    return null;
  }
};

// Derive floor from flat labels like "A-101" -> 1, "B-5-502" -> 5, "502" -> 5
const extractFloor = (label) => {
  if (!label) return null;
  const nums = String(label).match(/\d+/g);
  if (!nums || nums.length === 0) return null;
  const first = Number(nums[0]);
  if (!Number.isFinite(first)) return null;
  if (first >= 100) return Math.floor(first / 100); // 502 -> 5, 1102 -> 11
  return first; // e.g., "5-502" -> 5
};

// Get Date from Firestore TS/ISO/Date
const getDate = (ts) => {
  try {
    if (typeof ts?.toDate === "function") return ts.toDate();
    if (ts instanceof Date) return ts;
    if (typeof ts === "string") {
      const d = new Date(ts);
      return Number.isNaN(d.getTime()) ? null : d;
    }
  } catch {}
  return null;
};

// Household builder (residents + their family) for selected unit
const assembleHouseholdFromResidents = (residents, blockName, flatLabel) => {
  const unitResidents = residents.filter(
    (u) =>
      (u.unit?.blockName || "").trim() === (blockName || "").trim() &&
      (u.unit?.flatLabel || "").trim() === (flatLabel || "").trim()
  );

  const members = [];
  const seenKey = new Set();

  unitResidents.forEach((u, idx) => {
    const baseAge = getAge(u.age ?? u.dob ?? null);
    const baseName = u.fullName || u.displayName || u.uid || `Resident ${idx + 1}`;
    const baseKey = `${baseName}|${baseAge ?? "na"}|${u.gender || ""}|${u.uid || ""}`;
    if (!seenKey.has(baseKey)) {
      seenKey.add(baseKey);
      members.push({
        name: baseName,
        age: baseAge,
        gender: u.gender || "",
        isResident: true,
        uid: u.uid || null,
      });
    }

    if (Array.isArray(u.family)) {
      u.family.forEach((f, i) => {
        const fAge = getAge(f?.age ?? f?.dob ?? null);
        const fName = f?.name || `Family ${i + 1}`;
        const fKey = `${fName}|${fAge ?? "na"}|${f?.gender || ""}|${f?.id || ""}`;
        if (!seenKey.has(fKey)) {
          seenKey.add(fKey);
          members.push({
            name: fName,
            age: fAge,
            gender: f?.gender || "",
            relationship: f?.relationship || "",
            isResident: false,
            id: f?.id || null,
          });
        }
      });
    }
  });

  return members;
};

const classifyCounts = (members, freeAge) => {
  const ADULT_AGE = 18;
  let adults = 0;
  let kids = 0;
  let freeKids = 0;

  members.forEach((m) => {
    const age = m?.age ?? null;
    if (m?.isResident) {
      adults++;
      return;
    }
    if (age == null) return;
    if (age >= ADULT_AGE) adults++;
    else {
      kids++;
      if (freeAge != null && age < freeAge) freeKids++;
    }
  });

  return { adults, kids, freeKids };
};

/* ---------------------------------
   Component
---------------------------------- */

export default function FundsPanel({ communityId, event, community, residents, canEditFunds }) {
  const eventId = event?.id;

  const entryFee = event?.entryFee || { adult: 0, child: 0, freeBelowAge: null };
  const adultFee = Number(entryFee.adult) || 0;
  const childFee = Number(entryFee.child) || 0;
  const freeAge = entryFee.freeBelowAge == null ? null : Number(entryFee.freeBelowAge);

  // Live funds
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // UI tabs inside Funds: 'resident' | 'other'
  const [innerTab, setInnerTab] = useState("resident");

  // -------------------- Residents form state --------------------
  const [residentForm, setResidentForm] = useState({
    blockName: "",
    floorNumber: "",
    flatLabel: "",
    amountMode: "auto", // "auto" | "manual"
    amount: "",
    receiptNo: "",
    method: "",
    receivedByUid: "",
    receivedByName: "",
  });
  const [counts, setCounts] = useState({ adults: 0, kids: 0, freeKids: 0 });
  const [householdMembers, setHouseholdMembers] = useState([]);
  const [householdLoading, setHouseholdLoading] = useState(false);
  const [householdError, setHouseholdError] = useState("");
  const [showFamilyTable, setShowFamilyTable] = useState(false);

  // -------------------- Other funds (donation/gift/sale/other) --------------------
  const [otherForm, setOtherForm] = useState({
    type: "Donation", // Donation | Gift | Sale | Other
    title: "",        // optional title/subject
    notes: "",        // optional notes
    payerName: "",    // donor/buyer/gifter
    amount: "",
    receiptNo: "",
    method: "",
    receivedByUid: "",
    receivedByName: "",
  });

  // Derived totals
  const totalAll = funds.reduce((acc, f) => acc + (Number(f.amount) || 0), 0);
  const residentFunds = funds.filter((f) => (f.sourceType || "resident") === "resident");
  const otherFunds = funds.filter((f) => (f.sourceType || "resident") === "other");
  const totalResidents = residentFunds.reduce((acc, f) => acc + (Number(f.amount) || 0), 0);
  const totalOther = totalAll - totalResidents;

  /* ---------------------------------
     Options: Blocks → Floors → Flats
     (Union of community config + residents)
  ---------------------------------- */

  const blockOptions = useMemo(() => {
    const set = new Set();

    // Community-configured blocks
    const confBlocks = Array.isArray(community?.blocks) ? community.blocks : [];
    confBlocks.forEach((b, idx) => {
      const name = (b?.name || b?.blockName || `Block-${idx + 1}`).trim();
      if (name) set.add(name);
    });

    // Also include blocks observed in resident units
    residents.forEach((u) => {
      const b = (u.unit?.blockName || "").trim();
      if (b) set.add(b);
    });

    return Array.from(set).sort();
  }, [community, residents]);

  const floorOptions = useMemo(() => {
    const b = (residentForm.blockName || "").trim();
    if (!b) return [];

    const floors = new Set();

    // From community flats (parse floor)
    const confBlocks = Array.isArray(community?.blocks) ? community.blocks : [];
    const blk = confBlocks.find(
      (x, idx) => (x?.name || x?.blockName || `Block-${idx + 1}`).trim() === b
    );
    (Array.isArray(blk?.flats) ? blk.flats : []).forEach((label) => {
      const fl = extractFloor(label);
      if (fl != null) floors.add(String(fl));
    });

    // From residents (prefer unit.floorNumber)
    residents.forEach((u) => {
      if ((u.unit?.blockName || "").trim() === b) {
        const fl =
          Number.isFinite(u.unit?.floorNumber) && u.unit.floorNumber >= 0
            ? u.unit.floorNumber
            : extractFloor(u.unit?.flatLabel);
        if (fl != null) floors.add(String(fl));
      }
    });

    return Array.from(floors)
      .sort((a, b) => Number(a) - Number(b))
      .map((value) => ({ value, label: `Floor ${value}` }));
  }, [community, residents, residentForm.blockName]);

  const flatOptions = useMemo(() => {
    const b = (residentForm.blockName || "").trim();
    const f = (residentForm.floorNumber || "").trim();
    if (!b || !f) return [];

    const flatsSet = new Set();

    // Community: filter flats by parsed floor
    const confBlocks = Array.isArray(community?.blocks) ? community.blocks : [];
    const blk = confBlocks.find(
      (x, idx) => (x?.name || x?.blockName || `Block-${idx + 1}`).trim() === b
    );
    (Array.isArray(blk?.flats) ? blk.flats : []).forEach((label) => {
      const fl = extractFloor(label);
      if (String(fl ?? "") === f) {
        const lab = String(label).trim();
        if (lab) flatsSet.add(lab);
      }
    });

    // Residents: block + floor match
    residents.forEach((u) => {
      if ((u.unit?.blockName || "").trim() === b) {
        const ruFloor = Number.isFinite(u.unit?.floorNumber)
          ? String(u.unit.floorNumber)
          : String(extractFloor(u.unit?.flatLabel) ?? "");
        if (ruFloor === f && (u.unit?.flatLabel || "").trim()) {
          flatsSet.add((u.unit.flatLabel || "").trim());
        }
      }
    });

    return Array.from(flatsSet)
      .sort()
      .map((label) => ({
        value: label,
        label: label.includes(b) ? label : `${b}-${label}`,
      }));
  }, [community, residents, residentForm.blockName, residentForm.floorNumber]);

  /* ---------------------------------
     Household & Amount for Residents
  ---------------------------------- */

  // Load/sync household when block+flat selected
  useEffect(() => {
    const blockName = residentForm.blockName?.trim();
    const flatLabel = residentForm.flatLabel?.trim();

    setHouseholdError("");
    setHouseholdMembers([]);

    if (!blockName || !flatLabel) {
      setCounts({ adults: 0, kids: 0, freeKids: 0 });
      return;
    }

    setHouseholdLoading(true);
    try {
      const members = assembleHouseholdFromResidents(residents, blockName, flatLabel);
      setHouseholdMembers(members);

      if (members.length > 0) {
        const next = classifyCounts(members, freeAge);
        setCounts(next);
      } else {
        setCounts({ adults: 0, kids: 0, freeKids: 0 });
      }
    } catch (err) {
      console.error("Household build error:", err);
      setHouseholdError(err?.message || "Failed to load household.");
    } finally {
      setHouseholdLoading(false);
    }
  }, [residentForm.blockName, residentForm.flatLabel, residents, freeAge]);

  // Auto amount from counts + entry fee
  const autoAmountResidents = useMemo(
    () =>
      calculateAutoAmount({
        adultFee,
        childFee,
        adults: counts.adults,
        kids: counts.kids,
        freeKids: counts.freeKids,
      }),
    [counts.adults, counts.kids, counts.freeKids, adultFee, childFee]
  );

  // Keep Amount field synced when in auto mode (Residents)
  useEffect(() => {
    if (residentForm.amountMode === "auto") {
      setResidentForm((s) => ({ ...s, amount: autoAmountResidents ? String(autoAmountResidents) : "" }));
    }
  }, [autoAmountResidents, residentForm.amountMode]);

  /* ---------------------------------
     Live funds stream (single source)
  ---------------------------------- */
  useEffect(() => {
    if (!eventId) {
      setError("Missing eventId in URL.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    const qFunds = query(collection(db, "events", eventId, "funds"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      qFunds,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setFunds(rows);
        setLoading(false);
      },
      (err) => {
        console.error("Funds snapshot error:", err);
        setError(err?.message || "Failed to load funds.");
        setLoading(false);
      }
    );
    return () => unsub();
  }, [eventId]);

  /* ---------------------------------
     Actions (Add / Delete / Edit)
  ---------------------------------- */

  // Add resident fund
  const addResidentFund = useCallback(async () => {
    if (!canEditFunds) return alert("Not allowed for your role.");
    if (!eventId) return alert("Missing eventId; check the URL.");

    const {
      blockName, floorNumber, flatLabel,
      amountMode, amount, receiptNo, method, receivedByUid, receivedByName,
    } = residentForm;

    if (!blockName) return alert("Please select a Block.");
    if (!floorNumber) return alert("Please select a Floor.");
    if (!flatLabel) return alert("Please select a Flat.");

    const finalAmount = amountMode === "manual" ? Number(amount) : autoAmountResidents;
    if (!Number.isFinite(finalAmount) || !(finalAmount > 0)) {
      return alert("Amount must be > 0.");
    }

    try {
      const payload = cleanObject({
        sourceType: "resident",
        communityId: communityId || null,
        eventId,
        blockName: blockName.trim(),
        flatLabel: flatLabel.trim(),
        amount: finalAmount,
        receiptNo: (receiptNo || "").trim(),
        method: (method || "").trim(),
        // In resident collection we store collector in paidByName (as per your original logic)
        paidByName: (receivedByName || "").trim(),
        receivedByUid: typeof receivedByUid === "string" && receivedByUid ? receivedByUid : undefined,
        amountMode,
        householdCounts: {
          adults: Number(counts.adults) || 0,
          kids: Number(counts.kids) || 0,
          freeKids: Number(counts.freeKids) || 0,
        },
        entryFeeUsed: { adult: adultFee, child: childFee, freeBelowAge: freeAge },
        createdAt: serverTimestamp(),
      });

      await addDoc(collection(db, "events", eventId, "funds"), payload);

      // Reset
      setResidentForm({
        blockName: "",
        floorNumber: "",
        flatLabel: "",
        amountMode: "auto",
        amount: "",
        receiptNo: "",
        method: "",
        receivedByUid: "",
        receivedByName: "",
      });
      setCounts({ adults: 0, kids: 0, freeKids: 0 });
      setHouseholdMembers([]);
      setShowFamilyTable(false);
    } catch (err) {
      console.error("Add resident fund error:", err);
      alert(err?.message || "Failed to add resident fund.");
    }
  }, [
    canEditFunds, eventId, communityId,
    residentForm, counts, autoAmountResidents, adultFee, childFee, freeAge
  ]);

  // Add other fund (donation/gift/sale/other)
  const addOtherFund = useCallback(async () => {
    if (!canEditFunds) return alert("Not allowed for your role.");
    if (!eventId) return alert("Missing eventId; check the URL.");

    const { type, title, notes, payerName, amount, receiptNo, method, receivedByUid, receivedByName } = otherForm;

    const amt = Number(amount);
    if (!(amt > 0)) return alert("Amount must be > 0.");

    try {
      const payload = cleanObject({
        sourceType: "other",
        otherType: type || "Other",
        otherTitle: (title || "").trim() || undefined,
        otherNotes: (notes || "").trim() || undefined,
        communityId: communityId || null,
        eventId,
        amount: amt,
        receiptNo: (receiptNo || "").trim(),
        method: (method || "").trim(),
        paidByName: (payerName || "").trim() || (receivedByName || "").trim() || undefined,
        receivedByUid: typeof receivedByUid === "string" && receivedByUid ? receivedByUid : undefined,
        receivedByName: (receivedByName || "").trim() || undefined,
        amountMode: "manual",
        createdAt: serverTimestamp(),
      });

      await addDoc(collection(db, "events", eventId, "funds"), payload);

      // Reset
      setOtherForm({
        type: "Donation",
        title: "",
        notes: "",
        payerName: "",
        amount: "",
        receiptNo: "",
        method: "",
        receivedByUid: "",
        receivedByName: "",
      });
    } catch (err) {
      console.error("Add other fund error:", err);
      alert(err?.message || "Failed to add other fund.");
    }
  }, [canEditFunds, eventId, communityId, otherForm]);

  const deleteFund = useCallback(
    async (id) => {
      if (!canEditFunds) return alert("Not allowed for your role.");
      if (!eventId) return alert("Missing eventId; check the URL.");
      if (!window.confirm("Delete this fund entry?")) return;
      try {
        await deleteDoc(doc(db, "events", eventId, "funds", id));
      } catch (err) {
        console.error("Delete fund error:", err);
        alert(err?.message || "Failed to delete fund.");
      }
    },
    [eventId, canEditFunds]
  );

  /* ---------------------------------
     Excel: Resident Template + Bulk Upload
  ---------------------------------- */

  // Download resident template
  const downloadResidentTemplate = () => {
    const headers = [
      "blockName", "flatLabel",
      "adults", "kids", "freeKids",
      "amountMode", // "auto" or "manual"
      "amount",     // required only if amountMode=manual
      "receiptNo", "method",
      "receivedByUid", // optional
      "receivedByName" // optional
    ];

    const example = {
      blockName: "A",
      flatLabel: "A-101",
      adults: 2,
      kids: 1,
      freeKids: 0,
      amountMode: "auto",
      amount: "",
      receiptNo: "RCPT-001",
      method: "Cash",
      receivedByUid: "",
      receivedByName: "Collector Name",
    };

    const readme = [
      ["README (Resident Funds Upload Template)"],
      [""],
      ["Columns:"],
      ["- blockName (required)"],
      ["- flatLabel (required). Example formats: A-101, B-5-502 etc."],
      ["- adults (number, required for auto mode)"],
      ["- kids (number, required for auto mode)"],
      ["- freeKids (number, optional; kids below free age)"],
      ["- amountMode: 'auto' or 'manual'"],
      ["- amount: required if amountMode='manual' (numeric)"],
      ["- receiptNo (optional)"],
      ["- method: Cash, UPI, Google Pay, PhonePe, Bank Transfer (optional)"],
      ["- receivedByUid (optional, committee UID)"],
      ["- receivedByName (optional; if UID not provided)"],
      [""],
      [`Event Fee: Adult ₹${adultFee}, Child ₹${childFee}, Free below age: ${freeAge ?? "N/A"}`],
    ];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet([example], { header: headers });
    const ws2 = XLSX.utils.aoa_to_sheet(readme);

    XLSX.utils.book_append_sheet(wb, ws, "Residents");
    XLSX.utils.book_append_sheet(wb, ws2, "README");

    XLSX.writeFile(wb, `ResidentFundsTemplate_${event?.name || "Event"}.xlsx`);
  };

  // Bulk upload residents
  const handleResidentBulkUpload = async (file) => {
    if (!file) return;
    if (!eventId) return alert("Missing eventId; check the URL.");

    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: "array" });
      const ws = wb.Sheets["Residents"] || wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });

      if (!Array.isArray(rows) || rows.length === 0) {
        alert("No rows found in sheet.");
        return;
      }

      // Prepare batch
      const batch = writeBatch(db);
      let okCount = 0;
      let errCount = 0;

      for (const r of rows) {
        const blockName = String(r.blockName || "").trim();
        const flatLabel = String(r.flatLabel || "").trim();
        const adults = Number(r.adults || 0);
        const kids = Number(r.kids || 0);
        const freeKids = Number(r.freeKids || 0);
        const amountMode = String(r.amountMode || "auto").toLowerCase();
        const receiptNo = String(r.receiptNo || "").trim();
        const method = String(r.method || "").trim();
        const receivedByUid = String(r.receivedByUid || "").trim();
        const receivedByName = String(r.receivedByName || "").trim();

        // Basic validation
        if (!blockName || !flatLabel) { errCount++; continue; }

        let finalAmount = 0;
        if (amountMode === "manual") {
          finalAmount = Number(r.amount);
          if (!(finalAmount > 0)) { errCount++; continue; }
        } else {
          finalAmount = calculateAutoAmount({ adultFee, childFee, adults, kids, freeKids });
          if (!(finalAmount > 0)) { errCount++; continue; }
        }

        // Build payload (must match rules: numeric amount, strings for text fields)
        const payload = cleanObject({
          sourceType: "resident",
          communityId,
          eventId,
          blockName,
          flatLabel,
          amount: finalAmount,
          receiptNo,
          method,
          paidByName: receivedByName || undefined,
          receivedByUid: receivedByUid || undefined,
          amountMode: amountMode === "manual" ? "manual" : "auto",
          householdCounts: {
            adults: Number.isFinite(adults) ? adults : 0,
            kids: Number.isFinite(kids) ? kids : 0,
            freeKids: Number.isFinite(freeKids) ? freeKids : 0,
          },
          entryFeeUsed: { adult: adultFee, child: childFee, freeBelowAge: freeAge },
          createdAt: serverTimestamp(),
        });

        // Use auto-generated doc id
        const ref = doc(collection(db, "events", eventId, "funds"));
        batch.set(ref, payload);
        okCount++;
      }

      await batch.commit();
      alert(`Uploaded: ${okCount} rows. Skipped: ${errCount}.`);
    } catch (err) {
      console.error("Resident bulk upload error:", err);
      alert(err?.message || "Failed to upload.");
    }
  };

  /* ---------------------------------
     Edit Modal State
  ---------------------------------- */

  const [showEdit, setShowEdit] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editFund, setEditFund] = useState(null);
  const [editForm, setEditForm] = useState({
    // Shared
    amount: "",
    receiptNo: "",
    method: "",
    // Resident-only
    blockName: "",
    flatLabel: "",
    receivedByUid: "",
    paidByName: "",
    // Other-only
    otherType: "Donation",
    otherTitle: "",
    otherNotes: "",
    receivedByName: "",
    payerName: "", // maps to paidByName for other funds
  });

  const openEdit = (f) => {
    setEditFund(f);
    if ((f.sourceType || "resident") === "resident") {
      setEditForm({
        amount: String(f.amount ?? ""),
        receiptNo: f.receiptNo || "",
        method: f.method || "",
        blockName: f.blockName || "",
        flatLabel: f.flatLabel || "",
        receivedByUid: f.receivedByUid || "",
        paidByName: f.paidByName || "", // collector stored here in resident funds
        otherType: "Donation",
        otherTitle: "",
        otherNotes: "",
        receivedByName: "",
        payerName: "",
      });
    } else {
      setEditForm({
        amount: String(f.amount ?? ""),
        receiptNo: f.receiptNo || "",
        method: f.method || "",
        blockName: "",
        flatLabel: "",
        receivedByUid: f.receivedByUid || "",
        paidByName: f.paidByName || "",        // payer/donor (if set)
        otherType: f.otherType || "Other",
        otherTitle: f.otherTitle || "",
        otherNotes: f.otherNotes || "",
        receivedByName: f.receivedByName || "", // collector (other funds)
        payerName: f.paidByName || "",          // convenience field for editing
      });
    }
    setShowEdit(true);
  };

  const closeEdit = () => {
    setShowEdit(false);
    setSavingEdit(false);
    setEditFund(null);
  };

  const saveEdit = async () => {
    if (!canEditFunds) return alert("Not allowed for your role.");
    if (!eventId || !editFund?.id) return alert("Invalid fund selected.");

    const isResident = (editFund.sourceType || "resident") === "resident";
    const amt = Number(editForm.amount);
    if (!(amt > 0)) return alert("Amount must be > 0.");

    try {
      setSavingEdit(true);

      let payload;
      if (isResident) {
        payload = cleanObject({
          blockName: (editForm.blockName || "").trim(),
          flatLabel: (editForm.flatLabel || "").trim(),
          amount: amt,
          receiptNo: (editForm.receiptNo || "").trim(),
          method: (editForm.method || "").trim(),
          receivedByUid: (editForm.receivedByUid || "").trim() || undefined,
          paidByName: (editForm.paidByName || "").trim(), // collector
          updatedAt: serverTimestamp(),
        });
      } else {
        payload = cleanObject({
          otherType: (editForm.otherType || "Other").trim(),
          otherTitle: (editForm.otherTitle || "").trim() || undefined,
          otherNotes: (editForm.otherNotes || "").trim() || undefined,
          amount: amt,
          receiptNo: (editForm.receiptNo || "").trim(),
          method: (editForm.method || "").trim(),
          receivedByUid: (editForm.receivedByUid || "").trim() || undefined,
          receivedByName: (editForm.receivedByName || "").trim() || undefined, // collector
          paidByName: (editForm.payerName || editForm.paidByName || "").trim() || undefined, // donor/buyer
          updatedAt: serverTimestamp(),
        });
      }

      await updateDoc(doc(db, "events", eventId, "funds", editFund.id), payload);
      closeEdit();
    } catch (err) {
      console.error("Update fund error:", err);
      alert(err?.message || "Failed to update fund.");
      setSavingEdit(false);
    }
  };

  /* ---------------------------------
     Sorted views (ascending)
  ---------------------------------- */

  // Resident funds sorted: Block -> Floor -> Flat -> Receipt -> Created
  const sortedResidentFunds = useMemo(() => {
    return [...residentFunds].sort((a, b) => {
      const byBlock = cmpStr(a.blockName, b.blockName);
      if (byBlock !== 0) return byBlock;

      const af = extractFloor(a.flatLabel);
      const bf = extractFloor(b.flatLabel);
      if (Number.isFinite(af) || Number.isFinite(bf)) {
        if ((af ?? Number.POSITIVE_INFINITY) !== (bf ?? Number.POSITIVE_INFINITY)) {
          return (af ?? Number.POSITIVE_INFINITY) - (bf ?? Number.POSITIVE_INFINITY);
        }
      }

      const byFlat = cmpStr(a.flatLabel, b.flatLabel);
      if (byFlat !== 0) return byFlat;

      const byReceipt = cmpStr(a.receiptNo, b.receiptNo);
      if (byReceipt !== 0) return byReceipt;

      const ad = getDate(a.createdAt)?.getTime() ?? 0;
      const bd = getDate(b.createdAt)?.getTime() ?? 0;
      return ad - bd; // oldest first
    });
  }, [residentFunds]);

  // Other funds sorted: Type -> Title -> Payer/Donor -> Receipt -> Created
  const sortedOtherFunds = useMemo(() => {
    return [...otherFunds].sort((a, b) => {
      const byType = cmpStr(a.otherType, b.otherType);
      if (byType !== 0) return byType;

      const byTitle = cmpStr(a.otherTitle, b.otherTitle);
      if (byTitle !== 0) return byTitle;

      const byPayer = cmpStr(a.paidByName || a.receivedByName, b.paidByName || b.receivedByName);
      if (byPayer !== 0) return byPayer;

      const byReceipt = cmpStr(a.receiptNo, b.receiptNo);
      if (byReceipt !== 0) return byReceipt;

      const ad = getDate(a.createdAt)?.getTime() ?? 0;
      const bd = getDate(b.createdAt)?.getTime() ?? 0;
      return ad - bd; // oldest first
    });
  }, [otherFunds]);

  /* ---------------------------------
     Fee breakdown (unchanged)
  ---------------------------------- */

  const feeBreakdownResidents = (
    <div className="small mt-2">
      <div>
        Adults ({counts.adults}) × ₹{fmt(adultFee)} =
        {" "}<strong>₹ {fmt(adultFee * counts.adults)}</strong>
      </div>
      <div>
        Chargeable kids ({Math.max(0, counts.kids - counts.freeKids)}) × ₹{fmt(childFee)} =
        {" "}<strong>₹ {fmt(childFee * Math.max(0, counts.kids - counts.freeKids))}</strong>
      </div>
      <div>
        Total = <Badge bg="primary">₹ {fmt(autoAmountResidents)}</Badge>
      </div>
    </div>
  );

  /* ---------------------------------
     Render
  ---------------------------------- */
  return (
    <div>
      <div className="d-flex align-items-center justify-content-between">
        <h6 className="mb-3">Funds Collection</h6>
        <div className="d-flex align-items-center gap-2">
          <Badge bg="secondary">Residents: ₹ {fmt(totalResidents)}</Badge>
          <Badge bg="info">Other: ₹ {fmt(totalOther)}</Badge>
          <Badge bg="success">Total: ₹ {fmt(totalAll)}</Badge>
        </div>
      </div>

      {/* Inner tabs: Residents | Other Funds */}
      {canEditFunds && (
        <Nav variant="tabs" activeKey={innerTab} onSelect={(k) => k && setInnerTab(k)} className="mb-3">
          <Nav.Item><Nav.Link eventKey="resident">Residents</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="other">Other Funds (Donations / Gifts / Sales)</Nav.Link></Nav.Item>
        </Nav>
      )}

      {/* ----------------------- RESIDENTS TAB ----------------------- */}
      {innerTab === "resident" && (
        canEditFunds ? (
        <Card className="mb-3">
          <Card.Body>
            {/* Block -> Floor -> Flat */}
            <Row className="g-2 align-items-end">
              {/* Block */}
              <Col md={3}>
                <Form.Label>Block</Form.Label>
                <Form.Select
                  value={residentForm.blockName || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setResidentForm((s) => ({
                      ...s,
                      blockName: val,
                      floorNumber: "",
                      flatLabel: "",
                      amount: s.amountMode === "auto" ? "" : s.amount,
                    }));
                    setHouseholdMembers([]);
                    setCounts({ adults: 0, kids: 0, freeKids: 0 });
                  }}
                >
                  <option value="">— Select Block —</option>
                  {blockOptions.map((b) => (
                    <option key={`block-${b}`} value={b}>{b}</option>
                  ))}
                </Form.Select>
              </Col>

              {/* Floor */}
              <Col md={3}>
                <Form.Label>Floor</Form.Label>
                <Form.Select
                  value={residentForm.floorNumber || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setResidentForm((s) => ({ ...s, floorNumber: val, flatLabel: "" }));
                    setHouseholdMembers([]);
                    setCounts({ adults: 0, kids: 0, freeKids: 0 });
                  }}
                  disabled={!residentForm.blockName}
                >
                  <option value="">
                    {residentForm.blockName ? "— Select Floor —" : "Select block first"}
                  </option>
                  {floorOptions.map((opt) => (
                    <option key={`floor-${opt.value}`} value={opt.value}>{opt.label}</option>
                  ))}
                </Form.Select>
              </Col>

              {/* Flat */}
              <Col md={3}>
                <Form.Label>Flat</Form.Label>
                <Form.Select
                  value={residentForm.flatLabel || ""}
                  onChange={(e) => setResidentForm((s) => ({ ...s, flatLabel: e.target.value }))}
                  disabled={!residentForm.blockName || !residentForm.floorNumber}
                >
                  <option value="">
                    {residentForm.blockName && residentForm.floorNumber
                      ? "— Select Flat —"
                      : "Select block & floor first"}
                  </option>
                  {flatOptions.map((opt) => (
                    <option key={`flat-${opt.value}`} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              {/* Counts */}
              <Col md={3}>
                <Form.Label>Adults</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="1"
                  value={counts.adults}
                  onChange={(e) => setCounts((s) => ({ ...s, adults: Number(e.target.value) || 0 }))}
                />
              </Col>
              <Col md={3}>
                <Form.Label>Kids</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="1"
                  value={counts.kids}
                  onChange={(e) => setCounts((s) => ({ ...s, kids: Number(e.target.value) || 0 }))}
                />
              </Col>
              <Col md={3}>
                <Form.Label>
                  Kids ({freeAge != null ? `Free below ${freeAge} yrs` : "No free age"})
                </Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="1"
                  value={counts.freeKids}
                  onChange={(e) => setCounts((s) => ({ ...s, freeKids: Number(e.target.value) || 0 }))}
                />
              </Col>
            </Row>

            {/* Fee breakdown */}
            {feeBreakdownResidents}

            {/* Amount + Receipt + Pay Mode + Received By */}
            <Row className="g-2 align-items-end mt-2">
              <Col md={3}>
                <Form.Label className="d-block">Amount mode</Form.Label>
                {/* Radio buttons instead of select */}
                <div>
                  <Form.Check
                    inline
                    type="radio"
                    name="amountMode"
                    id="amountModeAuto"
                    label="Auto"
                    value="auto"
                    checked={residentForm.amountMode === "auto"}
                    onChange={(e) => setResidentForm((s) => ({ ...s, amountMode: e.target.value }))}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    name="amountMode"
                    id="amountModeManual"
                    label="Manual"
                    value="manual"
                    checked={residentForm.amountMode === "manual"}
                    onChange={(e) => setResidentForm((s) => ({ ...s, amountMode: e.target.value }))}
                  />
                </div>

                <Form.Label className="mt-2">Amount (₹)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  value={residentForm.amount}
                  onChange={(e) => setResidentForm((s) => ({ ...s, amount: e.target.value }))}
                  disabled={residentForm.amountMode !== "manual"}
                />
              </Col>

              <Col md={3}>
                <Form.Label>Receipt No</Form.Label>
                <Form.Control
                  value={residentForm.receiptNo}
                  onChange={(e) => setResidentForm((s) => ({ ...s, receiptNo: e.target.value }))}
                />
              </Col>

              <Col md={3}>
                <Form.Label>Pay Mode</Form.Label>
                <Form.Select
                  value={residentForm.method}
                  onChange={(e) => setResidentForm((s) => ({ ...s, method: e.target.value }))}
                >
                  <option value="">— Select —</option>
                  <option value="Cash">Cash</option>
                  <option value="PhonePe">PhonePe</option>
                  <option value="Google Pay">Google Pay</option>
                  <option value="UPI">UPI</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                </Form.Select>
              </Col>

              <Col md={3}>
                <Form.Label>Received By (UID)</Form.Label>
                <Form.Control
                  value={residentForm.receivedByUid}
                  onChange={(e) => setResidentForm((s) => ({ ...s, receivedByUid: e.target.value }))}
                  placeholder="optional committee UID"
                />
              </Col>
              <Col md={3}>
                <Form.Label>Received By (Name)</Form.Label>
                <Form.Control
                  value={residentForm.receivedByName}
                  onChange={(e) => setResidentForm((s) => ({ ...s, receivedByName: e.target.value }))}
                  placeholder="optional"
                />
              </Col>
            </Row>

            {/* Household details (shown only if resident(s) found) */}
            {(householdMembers.length > 0 || householdLoading || householdError) && (
              <Card className="mt-3" bg="light">
                <Card.Header className="d-flex align-items-center justify-content-between">
                  <span><strong>Family Details for Selected Flat</strong></span>
                  <Form.Check
                    type="switch"
                    id="show-family-table"
                    label={showFamilyTable ? "Hide details" : "Show details"}
                    checked={showFamilyTable}
                    onChange={(e) => setShowFamilyTable(e.target.checked)}
                  />
                </Card.Header>
                {showFamilyTable && (
                  <Card.Body>
                    {householdLoading && (
                      <div className="d-flex align-items-center mb-2">
                        <Spinner animation="border" size="sm" className="me-2" />
                        <span>Loading family…</span>
                      </div>
                    )}
                    {householdError && <Alert variant="danger">{householdError}</Alert>}
                    {householdMembers.length > 0 && (
                      <Table bordered responsive size="sm" className="mb-0">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Age</th>
                            <th>Classification</th>
                            <th>Gender</th>
                            <th>Relationship</th>
                          </tr>
                        </thead>
                        <tbody>
                          {householdMembers.map((m, idx) => {
                            const age = m?.age ?? null;
                            const ADULT_AGE = 18;
                            let label = "Unknown";
                            if (m?.isResident) label = "Resident (Adult)";
                            else if (age != null) {
                              if (age >= ADULT_AGE) label = "Adult";
                              else {
                                label = "Child";
                                if (freeAge != null && age < freeAge) label = "Child (Free)";
                              }
                            }
                            return (
                              <tr key={`member-${idx}`}>
                                <td>{m?.name || `Member ${idx + 1}`}</td>
                                <td>{age != null ? age : "—"}</td>
                                <td>{label}</td>
                                <td>{m?.gender || "—"}</td>
                                <td>{m?.relationship || "—"}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                )}
              </Card>
            )}

            {/* Submit + Excel actions */}
            <div className="mt-3 d-flex flex-wrap gap-2">
              <Button
                variant="primary"
                onClick={addResidentFund}
                disabled={
                  !residentForm.blockName ||
                  !residentForm.floorNumber ||
                  !residentForm.flatLabel ||
                  (residentForm.amountMode === "manual" && !residentForm.amount)
                }
              >
                Add Fund
              </Button>

              {/* Excel actions */}
              <Button variant="outline-secondary" onClick={downloadResidentTemplate}>
                Download Resident Template
              </Button>

              <Form.Label className="btn btn-outline-success mb-0">
                Upload Resident Excel
                <Form.Control
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: "none" }}
                  onChange={(e) => handleResidentBulkUpload(e.target.files?.[0])}
                />
              </Form.Label>
            </div>
          </Card.Body>
        </Card>
      ) : null
      )}

      {/* ----------------------- OTHER FUNDS TAB ----------------------- */}
      {innerTab === "other" && (
        <Card className="mb-3">
          <Card.Body>
            <Row className="g-2">
              <Col md={3}>
                <Form.Label>Type</Form.Label>
                <Form.Select
                  value={otherForm.type}
                  onChange={(e) => setOtherForm((s) => ({ ...s, type: e.target.value }))}
                >
                  <option>Donation</option>
                  <option>Gift</option>
                  <option>Sale</option>
                  <option>Other</option>
                </Form.Select>
              </Col>

              <Col md={3}>
                <Form.Label>Title (optional)</Form.Label>
                <Form.Control
                  placeholder="e.g., T-shirt sale, Sponsor"
                  value={otherForm.title}
                  onChange={(e) => setOtherForm((s) => ({ ...s, title: e.target.value }))}
                />
              </Col>

              <Col md={3}>
                <Form.Label>Payer / Donor</Form.Label>
                <Form.Control
                  placeholder="Name of donor/buyer"
                  value={otherForm.payerName}
                  onChange={(e) => setOtherForm((s) => ({ ...s, payerName: e.target.value }))}
                />
              </Col>

              <Col md={3}>
                <Form.Label>Amount (₹)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  value={otherForm.amount}
                  onChange={(e) => setOtherForm((s) => ({ ...s, amount: e.target.value }))}
                />
              </Col>

              <Col md={3}>
                <Form.Label>Receipt No</Form.Label>
                <Form.Control
                  value={otherForm.receiptNo}
                  onChange={(e) => setOtherForm((s) => ({ ...s, receiptNo: e.target.value }))}
                />
              </Col>

              <Col md={3}>
                <Form.Label>Pay Mode</Form.Label>
                <Form.Select
                  value={otherForm.method}
                  onChange={(e) => setOtherForm((s) => ({ ...s, method: e.target.value }))}
                >
                  <option value="">— Select —</option>
                  <option value="Cash">Cash</option>
                  <option value="PhonePe">PhonePe</option>
                  <option value="Google Pay">Google Pay</option>
                  <option value="UPI">UPI</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                </Form.Select>
              </Col>

              <Col md={3}>
                <Form.Label>Received By (UID)</Form.Label>
                <Form.Control
                  value={otherForm.receivedByUid}
                  onChange={(e) => setOtherForm((s) => ({ ...s, receivedByUid: e.target.value }))}
                  placeholder="optional committee UID"
                />
              </Col>

              <Col md={3}>
                <Form.Label>Received By (Name)</Form.Label>
                <Form.Control
                  value={otherForm.receivedByName}
                  onChange={(e) => setOtherForm((s) => ({ ...s, receivedByName: e.target.value }))}
                  placeholder="optional"
                />
              </Col>

              <Col md={12}>
                <Form.Label>Notes (optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Any extra info"
                  value={otherForm.notes}
                  onChange={(e) => setOtherForm((s) => ({ ...s, notes: e.target.value }))}
                />
              </Col>
            </Row>

            <div className="mt-3">
              <Button
                variant="primary"
                onClick={addOtherFund}
                disabled={!otherForm.amount}
              >
                Add Other Fund
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* ----------------------- LISTS ----------------------- */}
      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="d-flex align-items-center">
          <Spinner animation="border" size="sm" className="me-2" />
          <span>Loading funds…</span>
        </div>
      ) : (
        <>
          {/* Residents table */}
          <Card className="mb-3">
            <Card.Header className="d-flex align-items-center justify-content-between">
              <span><strong>Resident Funds</strong></span>
              <Badge bg="secondary">₹ {fmt(totalResidents)}</Badge>
            </Card.Header>
            <Card.Body className="p-0">
              <Table bordered hover responsive size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Block</th>
                    <th>Flat</th>
                    <th>Amount (₹)</th>
                    <th>Receipt</th>
                    <th>Method</th>
                    <th>Received By</th>
                    <th>Created</th>
                    {canEditFunds && <th style={{ width: 160 }}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {sortedResidentFunds.length === 0 ? (
                    <tr>
                      <td colSpan={canEditFunds ? 8 : 7} className="text-center">No resident funds yet</td>
                    </tr>
                  ) : (
                    sortedResidentFunds.map((f) => (
                      <tr key={f.id}>
                        <td>{f?.blockName || "—"}</td>
                        <td>{f?.flatLabel || "—"}</td>
                        <td>₹ {fmt(f?.amount)}</td>
                        <td>{f?.receiptNo || "—"}</td>
                        <td>{f?.method || "—"}</td>
                        <td>{f?.paidByName || "—"}</td>
                        <td>{fmtTs(f?.createdAt)}</td>
                        {canEditFunds && (
                          <td className="d-flex gap-2">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => openEdit(f)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => deleteFund(f.id)}
                            >
                              Delete
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* Other funds table */}
          <Card>
            <Card.Header className="d-flex align-items-center justify-content-between">
              <span><strong>Other Funds (Donations / Gifts / Sales)</strong></span>
              <Badge bg="info">₹ {fmt(totalOther)}</Badge>
            </Card.Header>
            <Card.Body className="p-0">
              <Table bordered hover responsive size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Title</th>
                    <th>Payer / Donor</th>
                    <th>Amount (₹)</th>
                    <th>Receipt</th>
                    <th>Method</th>
                    <th>Received By</th>
                    <th>Created</th>
                    {canEditFunds && <th style={{ width: 160 }}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {sortedOtherFunds.length === 0 ? (
                    <tr>
                      <td colSpan={canEditFunds ? 9 : 8} className="text-center">No other funds yet</td>
                    </tr>
                  ) : (
                    sortedOtherFunds.map((f) => (
                      <tr key={f.id}>
                        <td>{f?.otherType || "—"}</td>
                        <td>{f?.otherTitle || "—"}</td>
                        <td>{f?.paidByName || f?.receivedByName || "—"}</td>
                        <td>₹ {fmt(f?.amount)}</td>
                        <td>{f?.receiptNo || "—"}</td>
                        <td>{f?.method || "—"}</td>
                        <td>{f?.receivedByName || "—"}</td>
                        <td>{fmtTs(f?.createdAt)}</td>
                        {canEditFunds && (
                          <td className="d-flex gap-2">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => openEdit(f)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => deleteFund(f.id)}
                            >
                              Delete
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </>
      )}

      {/* Edit Modal */}
      <Modal show={showEdit} onHide={closeEdit} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Fund</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!editFund ? (
            <Alert variant="warning">No fund selected.</Alert>
          ) : (
            <>
              {(editFund.sourceType || "resident") === "resident" ? (
                <Row className="g-2">
                  <Col md={6}>
                    <Form.Label>Block</Form.Label>
                    <Form.Control
                      value={editForm.blockName}
                      onChange={(e) => setEditForm((s) => ({ ...s, blockName: e.target.value }))}
                      placeholder="e.g., A"
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Flat</Form.Label>
                    <Form.Control
                      value={editForm.flatLabel}
                      onChange={(e) => setEditForm((s) => ({ ...s, flatLabel: e.target.value }))}
                      placeholder="e.g., A-101"
                    />
                  </Col>

                  <Col md={4}>
                    <Form.Label>Amount (₹)</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={editForm.amount}
                      onChange={(e) => setEditForm((s) => ({ ...s, amount: e.target.value }))}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Receipt No</Form.Label>
                    <Form.Control
                      value={editForm.receiptNo}
                      onChange={(e) => setEditForm((s) => ({ ...s, receiptNo: e.target.value }))}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Pay Mode</Form.Label>
                    <Form.Select
                      value={editForm.method}
                      onChange={(e) => setEditForm((s) => ({ ...s, method: e.target.value }))}
                    >
                      <option value="">— Select —</option>
                      <option value="Cash">Cash</option>
                      <option value="PhonePe">PhonePe</option>
                      <option value="Google Pay">Google Pay</option>
                      <option value="UPI">UPI</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                    </Form.Select>
                  </Col>

                  <Col md={6}>
                    <Form.Label>Received By (UID)</Form.Label>
                    <Form.Control
                      value={editForm.receivedByUid}
                      onChange={(e) => setEditForm((s) => ({ ...s, receivedByUid: e.target.value }))}
                      placeholder="optional committee UID"
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Received By (Name)</Form.Label>
                    <Form.Control
                      value={editForm.paidByName}
                      onChange={(e) => setEditForm((s) => ({ ...s, paidByName: e.target.value }))}
                      placeholder="collector name"
                    />
                  </Col>
                </Row>
              ) : (
                <Row className="g-2">
                  <Col md={6}>
                    <Form.Label>Type</Form.Label>
                    <Form.Select
                      value={editForm.otherType}
                      onChange={(e) => setEditForm((s) => ({ ...s, otherType: e.target.value }))}
                    >
                      <option>Donation</option>
                      <option>Gift</option>
                      <option>Sale</option>
                      <option>Other</option>
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label>Title (optional)</Form.Label>
                    <Form.Control
                      value={editForm.otherTitle}
                      onChange={(e) => setEditForm((s) => ({ ...s, otherTitle: e.target.value }))}
                      placeholder="e.g., T-shirt sale, Sponsor"
                    />
                  </Col>

                  <Col md={4}>
                    <Form.Label>Amount (₹)</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={editForm.amount}
                      onChange={(e) => setEditForm((s) => ({ ...s, amount: e.target.value }))}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Receipt No</Form.Label>
                    <Form.Control
                      value={editForm.receiptNo}
                      onChange={(e) => setEditForm((s) => ({ ...s, receiptNo: e.target.value }))}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Pay Mode</Form.Label>
                    <Form.Select
                      value={editForm.method}
                      onChange={(e) => setEditForm((s) => ({ ...s, method: e.target.value }))}
                    >
                      <option value="">— Select —</option>
                      <option value="Cash">Cash</option>
                      <option value="PhonePe">PhonePe</option>
                      <option value="Google Pay">Google Pay</option>
                      <option value="UPI">UPI</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                    </Form.Select>
                  </Col>

                  <Col md={6}>
                    <Form.Label>Payer / Donor (Name)</Form.Label>
                    <Form.Control
                      value={editForm.payerName}
                      onChange={(e) => setEditForm((s) => ({ ...s, payerName: e.target.value }))}
                      placeholder="donor/buyer"
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Received By (Name)</Form.Label>
                    <Form.Control
                      value={editForm.receivedByName}
                      onChange={(e) => setEditForm((s) => ({ ...s, receivedByName: e.target.value }))}
                      placeholder="collector"
                    />
                  </Col>

                  <Col md={6}>
                    <Form.Label>Received By (UID)</Form.Label>
                    <Form.Control
                      value={editForm.receivedByUid}
                      onChange={(e) => setEditForm((s) => ({ ...s, receivedByUid: e.target.value }))}
                      placeholder="optional committee UID"
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Notes (optional)</Form.Label>
                    <Form.Control
                      value={editForm.otherNotes}
                      onChange={(e) => setEditForm((s) => ({ ...s, otherNotes: e.target.value }))}
                      placeholder="any extra info"
                    />
                  </Col>
                </Row>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEdit} disabled={savingEdit}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={saveEdit}
            disabled={
              savingEdit ||
              !editFund ||
              !editForm.amount ||
              (editFund?.sourceType === "resident" && (!editForm.blockName || !editForm.flatLabel))
            }
          >
            {savingEdit ? "Saving…" : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
